import { useRef, useEffect } from 'react';
import { getSetting } from '../../../../utils/settings';

/**
 * Selectors for scrollable elements that should handle wheel events natively
 */
const SCROLLABLE_SELECTORS = [
  // Menus and dropdowns
  '.task-node__status-menu',
  '.task-node__category-menu',
  '.task-node__drag-menu',
  '.filter-dropdown__menu',
  '.fokus-button__menu',
  '.kanban-filter-dropdown__menu',
  '.connection-menu',
  // Popovers
  '.artifact-popover',
  '.icon-picker',
  // Date picker
  '.date-picker__inline-container',
  '.date-time-popover',
  '.react-datepicker',
  // Modals
  '.decision-options-modal',
  '.notes-modal',
  '.blocked-reason-modal',
  '.confirm-modal',
  '.settings-modal',
  // Sidebars and summaries
  '.artifact-summary',
  '.task-detail-sidebar',
  // HUD mission lists
  '.flowchart-hud__missions-list',
  // Generic marker for scrollable elements
  '[data-scrollable]',
];

function canScrollVertically(el) {
  return Boolean(el) && el.scrollHeight > el.clientHeight + 1;
}

/** Wheel over a note with overflow scrolls the note instead of zooming the canvas. */
function isOverScrollableNote(target) {
  const note = target?.closest?.('.note-node');
  if (!note) return false;
  const text = target.closest?.('.note-node__text') || note.querySelector('.note-node__text');
  return canScrollVertically(text);
}

/**
 * useWheelHandler Hook
 * 
 * Handles wheel events for the flowchart:
 * - Trackpad two-finger scroll → Pan the view
 * - Mouse wheel / Pinch gesture → Zoom towards cursor
 * - Respects scrollable elements (menus, modals, etc.)
 */
export function useWheelHandler({
  containerRef,
  containerRectRef,
  zoomRef,
  panRef,
  constrainPan,
  panHook,
  setZoomState, // For debounced state sync after instant zoom
  getMinZoom,
  queueZoomToPoint,
  animatingZoomRef,
  targetZoomRef,
  isPanningRef, // Ref to check if user is currently panning/dragging
  // Sensitivity settings
  trackpadPanSensitivity,
  trackpadZoomSensitivity,
}) {
  // Store sensitivity settings in refs for the wheel handler
  const panSensitivityRef = useRef(trackpadPanSensitivity ?? getSetting('trackpadPanSensitivity'));
  const zoomSensitivityRef = useRef(trackpadZoomSensitivity ?? getSetting('trackpadZoomSensitivity'));
  
  // Debounce timer for syncing React state after instant zoom
  const zoomSyncTimeoutRef = useRef(null);
  
  // Debounce timeout for trackpad panning
  const trackpadPanTimeoutRef = useRef(null);
  
  // Update refs when props change
  useEffect(() => {
    if (trackpadPanSensitivity !== undefined) {
      panSensitivityRef.current = trackpadPanSensitivity;
    }
    if (trackpadZoomSensitivity !== undefined) {
      zoomSensitivityRef.current = trackpadZoomSensitivity;
    }
  }, [trackpadPanSensitivity, trackpadZoomSensitivity]);

  // Wheel handler stored in ref to avoid re-attaching listener
  const handleWheelRef = useRef(null);
  handleWheelRef.current = (e) => {
    // Check if target is inside any scrollable element
    const target = e.target;
    const isInsideScrollable = SCROLLABLE_SELECTORS.some(selector => 
      target.closest(selector)
    );
    
    if (isInsideScrollable || isOverScrollableNote(target)) {
      // Let the event propagate naturally for scrolling
      return;
    }
    
    e.preventDefault();
    
    // Get current sensitivity values
    const panSensitivity = panSensitivityRef.current;
    const zoomSensitivity = zoomSensitivityRef.current;
    
    // Detect if this is a trackpad two-finger scroll vs mouse wheel:
    // - Trackpad: deltaMode === 0 (pixel) and often has deltaX for horizontal movement
    // - Mouse wheel: deltaMode === 1 (line) or deltaMode === 0 with no deltaX
    // - Pinch gesture: ctrlKey is true
    const isTrackpadPan = e.deltaMode === 0 && Math.abs(e.deltaX) > 0 && !e.ctrlKey;
    
    if (isTrackpadPan) {
      handleTrackpadPan(e, panSensitivity);
    } else {
      handleWheelZoom(e, zoomSensitivity);
    }
  };

  // Ref for trackpad pan settle animation
  const trackpadSettleTimeoutRef = useRef(null);
  
  // Track if we've dispatched the pan start event for this gesture
  const hasPanStartedRef = useRef(false);
  
  /**
   * Handle trackpad two-finger scroll → Pan the view
   * Uses rubber-band effect at edges and settles back when scrolling stops
   */
  const handleTrackpadPan = (e, panSensitivity) => {
    const currentPan = panRef.current;
    const currentZoom = zoomRef.current;
    
    // Dispatch panstart event once at the beginning of a trackpad pan gesture
    // This allows popup menus to close themselves when panning starts
    if (!hasPanStartedRef.current) {
      hasPanStartedRef.current = true;
      window.dispatchEvent(new CustomEvent('flowchartpanstart'));
    }
    
    let dx = e.deltaX;
    let dy = e.deltaY;
    
    // Apply sensitivity
    dx *= panSensitivity;
    dy *= panSensitivity;
    
    const rawPanX = currentPan.x - dx;
    const rawPanY = currentPan.y - dy;
    
    const constrainedPan = constrainPan({ x: rawPanX, y: rawPanY }, currentZoom);
    
    // Apply "rubber band" effect: allow going past bounds but with resistance
    const RUBBER_BAND_FACTOR = 0.3;
    const overshootX = rawPanX - constrainedPan.x;
    const overshootY = rawPanY - constrainedPan.y;
    
    const smoothedPan = {
      x: constrainedPan.x + overshootX * RUBBER_BAND_FACTOR,
      y: constrainedPan.y + overshootY * RUBBER_BAND_FACTOR,
    };
    
    // PERFORMANCE: Update DOM directly for smooth trackpad panning
    const canvas = containerRef.current?.querySelector('.flowchart__canvas');
    if (canvas) {
      canvas.style.transform = `translate(${smoothedPan.x}px, ${smoothedPan.y}px) scale(${currentZoom})`;
      // Update CSS variable for zoom-independent elements (edge insert button, etc.)
      canvas.style.setProperty('--inverse-zoom', 1 / currentZoom);
      // Dispatch event for minimap to sync viewport
      canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: smoothedPan.x, y: smoothedPan.y, zoom: currentZoom } }));
    }
    
    // Update refs immediately so next frame uses correct values
    panRef.current = smoothedPan;
    // Also update panHook's panRef so pan drag starts from correct position
    if (panHook.panRef) {
      panHook.panRef.current = smoothedPan;
    }
    
    // Clear any existing settle timeout
    if (trackpadSettleTimeoutRef.current) {
      clearTimeout(trackpadSettleTimeoutRef.current);
    }
    
    // After scrolling stops, smoothly settle back to constrained position
    trackpadSettleTimeoutRef.current = setTimeout(() => {
      trackpadSettleTimeoutRef.current = null;
      // Reset pan started flag so next gesture can dispatch the event again
      hasPanStartedRef.current = false;
      // Check if we need to animate back to constrained position
      const finalConstrained = constrainPan(panRef.current, zoomRef.current);
      const needsCorrection = 
        Math.abs(finalConstrained.x - panRef.current.x) > 1 ||
        Math.abs(finalConstrained.y - panRef.current.y) > 1;
      
      if (needsCorrection) {
        // Animate back to constrained position
        const startPan = { ...panRef.current };
        const startTime = performance.now();
        const duration = 150;
        
        const animate = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(1, elapsed / duration);
          const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
          
          const animatedPan = {
            x: startPan.x + (finalConstrained.x - startPan.x) * eased,
            y: startPan.y + (finalConstrained.y - startPan.y) * eased,
          };
          
          panRef.current = animatedPan;
          // Also update panHook's panRef
          if (panHook.panRef) {
            panHook.panRef.current = animatedPan;
          }
          
          const canvas = containerRef.current?.querySelector('.flowchart__canvas');
          if (canvas) {
            canvas.style.transform = `translate(${animatedPan.x}px, ${animatedPan.y}px) scale(${zoomRef.current})`;
            canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: animatedPan.x, y: animatedPan.y, zoom: zoomRef.current } }));
          }
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // Skip DOM update, already done during animation
            panHook.setPan(animatedPan, true);
          }
        };
        
        requestAnimationFrame(animate);
      } else {
        // No correction needed, just sync React state (skip DOM, already up to date)
        panHook.setPan(panRef.current, true);
      }
    }, 100); // Wait 100ms after last scroll event
  };

  // Track if we've dispatched the zoom start event for this gesture
  const hasZoomStartedRef = useRef(false);
  
  /**
   * Handle mouse wheel or pinch gesture → Zoom towards cursor
   */
  const handleWheelZoom = (e, zoomSensitivity) => {
    // Skip zoom if user is currently panning/dragging to avoid conflicts
    if (isPanningRef?.current) return;
    
    const rect = containerRectRef.current;
    if (!rect || rect.width === 0) return;
    
    // Dispatch panstart event once at the beginning of a zoom gesture
    // This allows popup menus to close themselves when zooming starts
    if (!hasZoomStartedRef.current) {
      hasZoomStartedRef.current = true;
      window.dispatchEvent(new CustomEvent('flowchartpanstart'));
    }
    
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Normalize deltaY based on deltaMode
    let deltaY = e.deltaY;
    if (e.deltaMode === 1) { // LINE mode (mouse wheel)
      deltaY *= 20;
    } else if (e.deltaMode === 2) { // PAGE mode
      deltaY *= 100;
    }
    
    // Calculate zoom delta
    const zoomAmount = -deltaY * 0.002 * zoomSensitivity;
    const delta = Math.exp(zoomAmount);
    const minZoom = getMinZoom();
    
    const currentZoom = zoomRef.current;
    const newZoom = Math.max(minZoom, Math.min(currentZoom * delta, 2));
    
    // Instant zoom (direct DOM manipulation for performance)
    const currentPan = panRef.current;
    const zoomRatio = newZoom / currentZoom;
    const newPanX = mouseX - (mouseX - currentPan.x) * zoomRatio;
    const newPanY = mouseY - (mouseY - currentPan.y) * zoomRatio;
    
    // Apply constraint to keep content visible when zooming at edges
    const newPan = constrainPan({ x: newPanX, y: newPanY }, newZoom);
    
    // Update refs (both shared zoomRef AND panHook.zoomRef to keep them in sync)
    zoomRef.current = newZoom;
    panRef.current = newPan;
    // Also update panHook's refs so setPan/pan drag uses correct values
    if (panHook.zoomRef) {
      panHook.zoomRef.current = newZoom;
    }
    if (panHook.panRef) {
      panHook.panRef.current = newPan;
    }
    
    // Update DOM directly (no React state update during zoom for performance)
    const canvas = containerRef.current?.querySelector('.flowchart__canvas');
    if (canvas) {
      canvas.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${newZoom})`;
      canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: newPan.x, y: newPan.y, zoom: newZoom } }));
    }
    
    // Debounce React state sync - update state after zooming stops
    // This keeps React in sync without causing re-renders during active zooming
    if (zoomSyncTimeoutRef.current) {
      clearTimeout(zoomSyncTimeoutRef.current);
    }
    zoomSyncTimeoutRef.current = setTimeout(() => {
      // Sync React state with current ref values (skip DOM update, already up to date)
      setZoomState?.(zoomRef.current);
      panHook.setPan(panRef.current, true);
      zoomSyncTimeoutRef.current = null;
      // Reset zoom started flag so next gesture can dispatch the event again
      hasZoomStartedRef.current = false;
      
      // Update --inverse-zoom now that zooming has stopped
      const canvasEl = containerRef.current?.querySelector('.flowchart__canvas');
      if (canvasEl) {
        canvasEl.style.setProperty('--inverse-zoom', 1 / zoomRef.current);
      }
    }, 150); // 150ms after last zoom event
  };

  // Attach wheel listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const wheelHandler = (e) => handleWheelRef.current?.(e);
    container.addEventListener('wheel', wheelHandler, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', wheelHandler);
    };
  }, [containerRef]);

  return {
    // Expose for backwards compatibility (not used for actual wheel handling)
    handleWheel: () => {},
  };
}

export default useWheelHandler;

